/**
 * Add one proposal structure after the project has already been split.
 *
 * The new structure either becomes its own Files group (one more fork off each
 * whole-project root, or an empty track when that file type was never split) or
 * joins a group that already exists. Structures already in a group stay there.
 */

import type { ProjectFile, ProjectFileStream, ProjectFileStreamKind } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { structureSelectionLabel, structureSetKey } from './structureSelectionLabel';
import { groupKeyForStream } from './projectFileStreamGroups';
import { STREAM_KIND_RAIL_ORDER, structureTrackKindLabel } from './projectFileStructureTracks';
import {
  addForkToWholeProjectRoot,
  createProjectFileStream,
  renameProjectFileStructureGroup,
  updateProjectFileStreamStructures,
} from './projectFileService';
import {
  createProposalStructuresFromTimberLists,
  isLiveStructureRow,
  structureNamesForRow,
  type StructureFromTimberListResult,
  type StructureFromTimberListRow,
} from './proposalStructuresFromTimberLists';

/** Why this one new row cannot be created. Null when it is ready. */
export function newStructureRowProblem(
  row: StructureFromTimberListRow,
  structures: ReadonlyArray<StructureRef>,
): string | null {
  if (!isLiveStructureRow(row) || row.existingStructureId) return 'Name the new structure.';
  if (row.timberListStreamId && !row.timberListFileId) return 'Pick a version for the timber list (or choose None).';
  const names = structureNamesForRow(row);
  if (names.length === 0) return 'Name the new structure.';
  const taken = new Set(structures.map(s => s.name.trim().toLowerCase()));
  for (const name of names) {
    if (taken.has(name.toLowerCase())) {
      return `The proposal already has a structure named “${name}”.`;
    }
  }
  return null;
}

export type AddStructureDestination =
  | { kind: 'new'; /** Blank = the rail heading falls back to the structure names. */ groupLabel?: string }
  | { kind: 'existing'; structureIds: string[] };

export interface AddProjectStructureAfterSplitInput {
  dealId: string;
  projectId: string;
  row: StructureFromTimberListRow;
  streams: ReadonlyArray<ProjectFileStream>;
  files: ReadonlyArray<ProjectFile>;
  /** Proposal structures already on the deal, for the duplicate-name check. */
  structures: ReadonlyArray<StructureRef>;
  destination: AddStructureDestination;
  actor: { uid: string; name: string };
  fetchText?: (url: string) => Promise<string>;
  createStructures?: typeof createProposalStructuresFromTimberLists;
  addFork?: typeof addForkToWholeProjectRoot;
  tagTrack?: typeof updateProjectFileStreamStructures;
  renameGroup?: typeof renameProjectFileStructureGroup;
  createTrack?: typeof createProjectFileStream;
}

export interface AddProjectStructureAfterSplitResult {
  structures: StructureRef[];
  forkedStreamIds: string[];
  /** Empty tracks created for file types that were never split. */
  createdStreamIds: string[];
  taggedStreamIds: string[];
  failures: string[];
  focusStreamId: string | null;
}

function refsOnTrack(stream: ProjectFileStream): StructureRef[] {
  const ids = stream.structureIds ?? [];
  const names = stream.structureNames ?? [];
  return ids.filter(Boolean).map((id, i) => ({ id, name: (names[i] || '').trim() || id }));
}

function tracksInGroup(
  streams: ReadonlyArray<ProjectFileStream>,
  structureIds: readonly string[],
): ProjectFileStream[] {
  const key = structureSetKey(structureIds);
  return streams.filter(s => !!s.id && !s.wholeProjectRoot && groupKeyForStream(s) === key);
}

function mergeRefs(existing: StructureRef[], added: StructureRef[]): StructureRef[] {
  const out = [...existing];
  for (const s of added) {
    if (!out.some(o => o.id === s.id)) out.push(s);
  }
  return out;
}

const kindRank = (kind: ProjectFileStreamKind) => {
  const index = STREAM_KIND_RAIL_ORDER.indexOf(kind);
  return index === -1 ? STREAM_KIND_RAIL_ORDER.length : index;
};

export async function addProjectStructureAfterSplit(
  input: AddProjectStructureAfterSplitInput,
): Promise<AddProjectStructureAfterSplitResult> {
  const { dealId, projectId, row, streams, files, structures, destination, actor } = input;
  const createStructures = input.createStructures ?? createProposalStructuresFromTimberLists;
  const addFork = input.addFork ?? addForkToWholeProjectRoot;
  const tagTrack = input.tagTrack ?? updateProjectFileStreamStructures;
  const renameGroup = input.renameGroup ?? renameProjectFileStructureGroup;
  const createTrack = input.createTrack ?? createProjectFileStream;

  const result: AddProjectStructureAfterSplitResult = {
    structures: [],
    forkedStreamIds: [],
    createdStreamIds: [],
    taggedStreamIds: [],
    failures: [],
    focusStreamId: null,
  };

  const problem = newStructureRowProblem(row, structures);
  if (problem) throw new Error(problem);

  let joinMembers: ProjectFileStream[] = [];
  if (destination.kind === 'existing') {
    if (destination.structureIds.filter(Boolean).length === 0) {
      throw new Error('Choose a structure group.');
    }
    joinMembers = tracksInGroup(streams, destination.structureIds);
    if (joinMembers.length === 0) throw new Error('That structure group is no longer on this project.');
  }

  const created: StructureFromTimberListResult = await createStructures({
    dealId,
    projectId,
    rows: [row],
    streams,
    files,
    actor,
    fetchText: input.fetchText,
  });
  result.failures.push(...created.failures);
  const structuresMade = created.structuresByRow[row.key] ?? created.created;
  result.structures = structuresMade;
  if (structuresMade.length === 0) return result;

  const autoLabel = structureSelectionLabel(structuresMade.map(s => s.name), { maxNamed: 3 });
  const timberIds = Array.from(new Set(created.taggedStreams.map(t => t.streamId).filter(Boolean)));

  if (destination.kind === 'new') {
    const customLabel = (destination.groupLabel || '').trim();
    const heading = customLabel || autoLabel;
    const roots = streams.filter(s => !!s.id && s.wholeProjectRoot && s.streamKind !== 'timber_list_csv');
    const rootedKinds = new Set(roots.map(s => s.streamKind));
    const orphanKinds = new Set<ProjectFileStreamKind>();
    for (const s of streams) {
      if (!s.id || s.wholeProjectRoot) continue;
      if ((s.structureIds ?? []).filter(Boolean).length === 0) continue;
      if (s.streamKind === 'timber_list_csv') continue;
      if (rootedKinds.has(s.streamKind)) continue;
      orphanKinds.add(s.streamKind);
    }

    for (const root of roots) {
      try {
        const fork = await addFork(
          projectId,
          root.id!,
          {
            title: `${heading} — ${root.title}`,
            structures: structuresMade,
            ...(customLabel ? { structureGroupLabel: customLabel } : {}),
          },
          actor,
        );
        result.forkedStreamIds.push(fork.forkedStreamId);
      } catch (err) {
        result.failures.push(`${root.title}: ${err instanceof Error ? err.message : 'could not add a track for this group'}`);
      }
    }

    const kinds = Array.from(orphanKinds).sort((a, b) => kindRank(a) - kindRank(b));
    for (const kind of kinds) {
      try {
        const track = await createTrack(
          projectId,
          `${heading} — ${structureTrackKindLabel(kind)}`,
          kind,
          actor.uid,
          undefined,
          {
            structures: structuresMade,
            ...(customLabel ? { structureGroupLabel: customLabel } : {}),
          },
        );
        if (track.id) result.createdStreamIds.push(track.id);
      } catch (err) {
        result.failures.push(
          `${structureTrackKindLabel(kind)}: ${err instanceof Error ? err.message : 'could not create a track for this group'}`,
        );
      }
    }

    if (customLabel && timberIds.length) {
      try {
        await renameGroup(projectId, timberIds, customLabel);
      } catch (err) {
        result.failures.push(err instanceof Error ? err.message : 'Could not name the new group.');
      }
    }

    result.focusStreamId = result.forkedStreamIds[0] ?? result.createdStreamIds[0] ?? timberIds[0] ?? null;
    return result;
  }

  const customLabel = joinMembers.map(s => (s.structureGroupLabel || '').trim()).find(Boolean) || '';
  const existingRefs = refsOnTrack(joinMembers[0]);
  const combined = mergeRefs(existingRefs, structuresMade);
  const ids = Array.from(new Set([...joinMembers.map(s => s.id!), ...timberIds]));
  for (const streamId of ids) {
    const title = streams.find(s => s.id === streamId)?.title || 'Track';
    try {
      await tagTrack(projectId, streamId, combined);
      result.taggedStreamIds.push(streamId);
    } catch (err) {
      result.failures.push(`${title}: ${err instanceof Error ? err.message : 'could not add the structure to this group'}`);
    }
  }
  if (customLabel && result.taggedStreamIds.length) {
    try {
      await renameGroup(projectId, result.taggedStreamIds, customLabel);
    } catch (err) {
      result.failures.push(err instanceof Error ? err.message : 'Could not keep the group name.');
    }
  }
  result.focusStreamId = timberIds[0] ?? result.taggedStreamIds[0] ?? null;
  return result;
}
